import React, { useCallback, useEffect, useState } from "react";
import {
	Box,
	Card,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Button,
	CardContent,
	TablePagination,
} from "@mui/material";
import { Grid } from "@material-ui/core";
import dayjs from "dayjs";
import filter from "../../../assets/images/adminSection/filter.svg";
import CustomDatePicker from "../../../components/common/customDatePicker";
import BikeDiscountEmployeeSelect from "../../layout/bikeDiscountEmployeeSelect";
import { UserOption } from "../../../components/AutoCompleteUserSelect/AutoCompleteUserSelect";
import bikeDiscountService from "../../../services/bikeDiscountService";
import {
	BikeDiscountErrorLogFilters,
	BikeDiscountErrorLogRow,
} from "../../../types/bikeDiscount";
import { showToast } from "../../../components/common/toastNotification";
import {
	toUtcDateRangeEnd,
	toUtcDateRangeStart,
} from "../../../helper/getDateRangeFilter";

interface FilterFormState {
	fromDate: string;
	toDate: string;
}

const initialFilterForm: FilterFormState = {
	fromDate: "",
	toDate: "",
};

const getEmployeeNameFromSelection = (employee: UserOption | null) => {
	if (!employee?.name) return null;
	const [fullName] = employee.name.split(" | ");
	return fullName?.trim() || null;
};

const formatCellValue = (value?: string | number | null) => {
	const text = value?.toString().trim();
	return text ? text : "-";
};

const mapErrorLogRow = (
	row: Record<string, string | number | null | undefined>,
): BikeDiscountErrorLogRow => ({
	applicationId: String(row.applicationId ?? row.applicationID ?? row.employeeId ?? ""),
	employeeName: String(row.employeeName ?? ""),
	errorMessage: String(row.errorMessage ?? row.errorMsg ?? row.message ?? ""),
	retryCount: Number(row.retryCount ?? row.retry ?? 0),
});

const BikeDiscountErrorLogDashboard: React.FC = () => {
	const [tableData, setTableData] = useState<BikeDiscountErrorLogRow[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [showFilters, setShowFilters] = useState(false);
	const [formData, setFormData] = useState<FilterFormState>(initialFilterForm);
	const [selectedEmployee, setSelectedEmployee] = useState<UserOption | null>(null);
	const [appliedFilters, setAppliedFilters] = useState<BikeDiscountErrorLogFilters>({});

	const buildApiPayload = useCallback(
		(filters: BikeDiscountErrorLogFilters): BikeDiscountErrorLogFilters => ({
			fromDate: filters.fromDate || null,
			toDate: filters.toDate || null,
			employeeName: filters.employeeName || null,
			pageNumber: page,
			pageSize,
		}),
		[page, pageSize],
	);

	const fetchErrorLogs = useCallback(
		async (filters: BikeDiscountErrorLogFilters = appliedFilters) => {
			try {
				const response = await bikeDiscountService.getAdminErrorLog(buildApiPayload(filters));
				const responseData = response?.data?.data ?? response?.data;
				const logs =
					responseData?.errorLogs ??
					responseData?.records ??
					responseData?.data ??
					(Array.isArray(responseData) ? responseData : []);

				setTableData(logs.map((row: Record<string, string | number>) => mapErrorLogRow(row)));
				setTotalCount(
					responseData?.totalRecords ??
						responseData?.totalCount ??
						responseData?.totalFilteredCount ??
						0,
				);
			} catch (error) {
				console.error("Failed to fetch bike discount error logs", error);
				setTableData([]);
				setTotalCount(0);
			}
		},
		[appliedFilters, buildApiPayload],
	);

	useEffect(() => {
		fetchErrorLogs();
	}, [page, pageSize, appliedFilters]);

	const handleSearch = () => {
		if (formData.fromDate && formData.toDate) {
			if (dayjs(formData.fromDate).isAfter(dayjs(formData.toDate))) {
				showToast("From date cannot be after to date.", "error");
				return;
			}
		}

		setAppliedFilters({
			fromDate: formData.fromDate ? toUtcDateRangeStart(formData.fromDate) : null,
			toDate: formData.toDate ? toUtcDateRangeEnd(formData.toDate) : null,
			employeeName: getEmployeeNameFromSelection(selectedEmployee),
		});
		setPage(0);
	};

	const handleClearFilters = () => {
		setFormData(initialFilterForm);
		setSelectedEmployee(null);
		setAppliedFilters({});
		setPage(0);
	};

	const handleDateFromChange = (date: dayjs.Dayjs | null) => {
		if (date?.isAfter(dayjs(), "day")) {
			showToast("From date cannot be a future date.", "error");
			return;
		}

		setFormData((prev) => {
			const fromDate = date ? date.format("YYYY-MM-DD") : "";
			const shouldClearToDate =
				fromDate && prev.toDate && dayjs(prev.toDate).isBefore(dayjs(fromDate), "day");

			return {
				...prev,
				fromDate,
				toDate: shouldClearToDate ? "" : prev.toDate,
			};
		});
	};

	const handleDateToChange = (date: dayjs.Dayjs | null) => {
		if (date?.isAfter(dayjs(), "day")) {
			showToast("To date cannot be a future date.", "error");
			return;
		}

		if (date && formData.fromDate && date.isBefore(dayjs(formData.fromDate), "day")) {
			showToast("To date cannot be before from date.", "error");
			return;
		}

		setFormData((prev) => ({
			...prev,
			toDate: date ? date.format("YYYY-MM-DD") : "",
		}));
	};

	const disableFutureDate = (date: dayjs.Dayjs) => date.isAfter(dayjs(), "day");

	const disableInvalidToDate = (date: dayjs.Dayjs) =>
		date.isAfter(dayjs(), "day") ||
		(formData.fromDate ? date.isBefore(dayjs(formData.fromDate), "day") : false);

	return (
		<Box p={3}>
			<Card
				sx={{
					backgroundColor: "#212121",
					mb: 2,
					borderRadius: 0,
					boxShadow: "none",
				}}>
				<CardContent sx={{ py: 2, "&:last-child": { paddingBottom: 2 } }}>
					<Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "16px" }}>
						Bike Discount - Error Logs
					</Typography>
				</CardContent>
			</Card>

			<Card
				sx={{
					backgroundColor: "#212121",
					mb: 2,
					borderRadius: 0,
					boxShadow: "none",
				}}>
				<CardContent>
					<Grid
						container
						style={{
							marginBottom: 8,
							alignItems: "center",
							justifyContent: "flex-end",
						}}>
						<Button
							className="filterBtn f-14"
							style={{
								minWidth: 160,
								maxWidth: 160,
								display: "flex",
								justifyContent: "space-between",
								padding: "0 15px",
							}}
							onClick={() => setShowFilters((prev) => !prev)}>
							<Typography>Filter</Typography>
							<img src={filter} alt="filter" />
						</Button>
					</Grid>

					{showFilters && (
						<Box
							sx={{
								backgroundColor: "#5D5D5D",
								padding: 2,
								borderRadius: "4px",
								marginBottom: 2,
							}}>
							<Grid container spacing={2}>
								<Grid item xs={6} md={2}>
									<CustomDatePicker
										name="fromDate"
										label="From Date"
										backgroundColor="#5D5D5D"
										placeholder="Select"
										value={formData.fromDate ? dayjs(formData.fromDate) : null}
										shouldDisableDate={disableFutureDate}
										onChange={handleDateFromChange}
									/>
								</Grid>
								<Grid item xs={6} md={2}>
									<CustomDatePicker
										name="toDate"
										label="To Date"
										backgroundColor="#5D5D5D"
										placeholder="Select"
										value={formData.toDate ? dayjs(formData.toDate) : null}
										shouldDisableDate={disableInvalidToDate}
										onChange={handleDateToChange}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<BikeDiscountEmployeeSelect
										label="Employee Name / Code"
										placeholder="Search employee name or code"
										backgroundColor="#5D5D5D"
										value={selectedEmployee}
										onChange={setSelectedEmployee}
									/>
								</Grid>
								<Grid
									item
									xs={12}
									style={{
										display: "flex",
										justifyContent: "flex-end",
										alignItems: "center",
										gap: 16,
									}}>
									<Button
										className="otherButton f-14"
										variant="contained"
										style={{
											borderRadius: 0,
											backgroundColor: "#5D5D5D",
											width: 140,
										}}
										onClick={handleSearch}>
										Search
									</Button>
									<Button
										className="otherButton f-14"
										variant="contained"
										style={{
											borderRadius: 0,
											backgroundColor: "#5D5D5D",
											width: 140,
										}}
										onClick={handleClearFilters}>
										Clear
									</Button>
								</Grid>
							</Grid>
						</Box>
					)}

					<TableContainer
						sx={{
							backgroundColor: "#212121",
							padding: 0,
							minHeight: "300px",
						}}>
						<Table>
							<TableHead>
								<TableRow>
									{["Application ID", "Employee Name", "Error message", "Retry Count"].map(
										(header) => (
											<TableCell
												key={header}
												sx={{
													color: "#fff",
													background: "#0A0A0A",
													fontWeight: "600",
													whiteSpace: "nowrap",
													py: 2.5,
													borderBottom: "1px solid rgb(87, 87, 87)",
												}}>
												{header}
											</TableCell>
										),
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{tableData.length === 0 ? (
									<TableRow>
										<TableCell
											colSpan={4}
											sx={{
												color: "#fff",
												textAlign: "center",
												py: 2.5,
												borderBottom: "1px solid rgb(87, 87, 87)",
											}}>
											No data available.
										</TableCell>
									</TableRow>
								) : (
									tableData.map((row, index) => (
										<TableRow key={`${row.applicationId}-${index}`}>
											<TableCell
												sx={{
													color: "#fff",
													py: 2.5,
													borderBottom: "1px solid rgb(87, 87, 87)",
												}}>
												{formatCellValue(row.applicationId)}
											</TableCell>
											<TableCell
												sx={{
													color: "#fff",
													py: 2.5,
													borderBottom: "1px solid rgb(87, 87, 87)",
												}}>
												{formatCellValue(row.employeeName)}
											</TableCell>
											<TableCell
												sx={{
													color: "#fff",
													py: 2.5,
													borderBottom: "1px solid rgb(87, 87, 87)",
												}}>
												{formatCellValue(row.errorMessage)}
											</TableCell>
											<TableCell
												sx={{
													color: "#fff",
													py: 2.5,
													borderBottom: "1px solid rgb(87, 87, 87)",
												}}>
												{formatCellValue(row.retryCount)}
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Grid
						style={{
							justifyContent: "flex-end",
							alignItems: "center",
							display: "flex",
							marginTop: 16,
							padding: 8,
						}}>
						<TablePagination
							rowsPerPageOptions={[5, 10, 25, 50, 100]}
							component="div"
							count={totalCount}
							rowsPerPage={pageSize}
							page={page}
							onPageChange={(_, newPage) => setPage(newPage)}
							onRowsPerPageChange={(e) => {
								setPageSize(parseInt(e.target.value, 10));
								setPage(0);
							}}
							sx={{
								color: "#ffffff",
								"& .MuiSelect-icon": { color: "#ffffff" },
								"& .MuiSvgIcon-root": { color: "#ffffff" },
							}}
						/>
					</Grid>
				</CardContent>
			</Card>
		</Box>
	);
};

export default BikeDiscountErrorLogDashboard;
